<script setup> 
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import { ref, computed } from "vue";
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import BreezeButton from '@/Components/Button.vue';
import BreezeInput from '@/Components/Input.vue';
import BreezeLabel from '@/Components/Label.vue';
import BreezeValidationErrors from '@/Components/ValidationErrors.vue';
import SelectSearch from '@/Components/SelectSearch.vue';
import { Inertia } from '@inertiajs/inertia';
import { Head, Link, useForm } from '@inertiajs/inertia-vue3';
import BaseButton from "@/Components/BaseButton.vue";
import CardBoxModal from "@/Components/CardBoxModal.vue";

const props = defineProps({
    origin_type: String,
    object_target: Array,
    action: {
        type: String,
        default: 'create'
    },
    object: Object,
    limit_file_size_kb: {
        type: Number,
        default: 5000
    },
    files_date_diff_days: {
        type: Number,
        default: 15
    }
});

const rutaPrefijo = ref('file');
const src_type = props.origin_type;
const actionText = ref('Actualizar');
const showUploadUpdate = ref(true);
if (props.action == 'create') actionText.value = 'Registrar';

const limitSizeMB = computed(() => Math.round((props.limit_file_size_kb || 5000) / 1000));
const sizeError = ref('');

const formatDateToISO = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const getTodayDate = () => formatDateToISO(new Date());

const getDefaultEndDate = (diffDays = 15) => {
    const d = new Date();
    d.setDate(d.getDate() + parseInt(diffDays || 15));
    return formatDateToISO(d);
};

const form = useForm({
    id: props.object ? props.object.id : 0,
    file: null,
    files: [],
    filename: '',
    oldfilepath: props.object ? props.object.filepath : '',
    oldfilename: props.object ? props.object.filename : '',
    originName: '',
    src_object_type: src_type,
    old_src_object_id: props.object ? props.object.src_object_id : 0,
    src_object_id: props.object ? props.object.src_object_id : 0,
    name: props.object ? props.object.name : '',
    description: props.object ? props.object.description : '',
    position: props.object ? props.object.position : 1,
    start_date: props.object && props.object.start_date ? props.object.start_date : getTodayDate(),
    end_date: props.object && props.object.end_date ? props.object.end_date : getDefaultEndDate(props.files_date_diff_days),
    terms: false,
    type: ''
});

// Opciones para el SelectSearch
const targetOptions = computed(() => {
    const defaultOption = { id: 0, label: 'Compartido con todos', label_object: 'Compartido con todos', is_parent: true, bold: true };
    const list = [defaultOption];
    if (props.object_target && Array.isArray(props.object_target)) {
        props.object_target.forEach(item => {
            list.push({
                ...item,
                label: item.label || item.label_object
            });
        });
    }
    return list;
});

// Drag & Drop y manejo de archivos
const isDragging = ref(false);
const fileInputRef = ref(null);
const selectedFiles = ref([]);
const isImagen = ref(false);
const imgMiniatura = ref('');

const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const processFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    sizeError.value = '';
    
    const maxSizeBytes = (props.limit_file_size_kb || 5000) * 1024;
    const filesArray = [];
    const rejectedFiles = [];

    Array.from(fileList).forEach(f => {
        if (f.size > maxSizeBytes) {
            rejectedFiles.push(f.name);
        } else {
            filesArray.push(f);
        }
    });

    if (rejectedFiles.length > 0) {
        sizeError.value = `Los siguientes archivos superan el límite de ${limitSizeMB.value}MB y no fueron agregados: ${rejectedFiles.join(', ')}`;
    }

    if (filesArray.length === 0) return;

    selectedFiles.value = filesArray;
    form.files = filesArray;
    form.file = filesArray[0];

    if (filesArray.length === 1) {
        const file = filesArray[0];
        const rawName = file.name || '';
        const nameWithoutExt = rawName.substring(0, rawName.lastIndexOf('.')) || rawName;
        form.name = nameWithoutExt;
        if (!form.position) form.position = 1;

        if (file.type && file.type.startsWith('image/')) {
            isImagen.value = true;
            fileUp(file);
        } else {
            isImagen.value = false;
            imgMiniatura.value = '';
        }
    } else {
        // Múltiples archivos
        form.name = filesArray[0].name.substring(0, filesArray[0].name.lastIndexOf('.')) || filesArray[0].name;
        form.description = '';
        form.position = 1;
        isImagen.value = false;
        imgMiniatura.value = '';
    }
};

const handleFileInputChange = (event) => {
    processFiles(event.target.files);
};

const handleDrop = (event) => {
    isDragging.value = false;
    if (event.dataTransfer && event.dataTransfer.files) {
        processFiles(event.dataTransfer.files);
    }
};

const removeFile = (index) => {
    selectedFiles.value.splice(index, 1);
    form.files = [...selectedFiles.value];
    if (selectedFiles.value.length === 0) {
        form.file = null;
        form.name = '';
        isImagen.value = false;
        imgMiniatura.value = '';
    } else if (selectedFiles.value.length === 1) {
        processFiles(selectedFiles.value);
    }
};

const fileUp = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        imgMiniatura.value = e.target.result;
    };
    reader.readAsDataURL(file);
};

const deleteable = ref(true);
const rowID = ref(0);
const isModalDangerActive = ref(false);
const rowAction = ref('');

const initModal = (id, action) => {
    rowAction.value = action;
    rowID.value = id;
    isModalDangerActive.value = true;
};

const isConfirm = () => {
    const ruta = rutaPrefijo.value + '-' + rowAction.value;
    if (rowAction.value == 'eliminar') {
        Inertia.post(route(ruta, { id: rowID.value }));
    }
    cleanVal();
};

const isCancel = () => {
    cleanVal();
};

const cleanVal = () => {
    rowID.value = 0;
    rowAction.value = '';
};

const submit = () => {
    if (props.action == 'create') {
        form.post(route(rutaPrefijo.value + '-registrar'), {
            forceFormData: true,
        });
    } else {
        form.post(route(rutaPrefijo.value + '-actualizar'), {
            forceFormData: true,
        });
    }
};
</script>

<template>
    <Head :title="actionText + ' documento'" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader 
                :title="origin_type == 'file_forlevel' ? actionText + ' Documento por nivel' : (origin_type == 'file_resourceforcourse' ? actionText + ' Recursos de estudio' : actionText + ' Documento')" 
                subtitle="Gestión de documentos">
            </PremiumPageHeader>
        </template>

        <div class="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div class="bg-white dark:bg-slate-900 overflow-visible shadow-sm sm:rounded-2xl border border-gray-200 dark:border-slate-800">
                <div class="p-6 md:p-8 bg-white dark:bg-slate-900 rounded-2xl overflow-visible">
                    <BreezeValidationErrors class="mb-6" />

                    <div v-if="sizeError" class="mb-6 p-4 rounded-xl bg-amber-50 text-amber-800 text-sm border border-amber-200 flex items-center gap-2">
                        <i class="fa-solid fa-triangle-exclamation text-amber-600"></i>
                        <span>{{ sizeError }}</span>
                    </div>

                    <!-- Formulario de Creación -->
                    <template v-if="action == 'create'">
                        <form @submit.prevent="submit" enctype="multipart/form-data" class="space-y-6">
                            
                            <!-- 1. DRAG AND DROP / SUBIR ARCHIVO (PRIMER LUGAR) -->
                            <div>
                                <BreezeLabel for="file" value="Subir archivo(s)" :required="true" class="mb-2 block font-semibold text-gray-700 dark:text-gray-200" />
                                
                                <div
                                    class="relative border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-200 flex flex-col items-center justify-center cursor-pointer bg-gray-50/60 dark:bg-slate-800/40 hover:bg-blue-50/50 dark:hover:bg-slate-800/80"
                                    :class="isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-[0.99]' : 'border-gray-300 dark:border-slate-700'"
                                    @dragover.prevent="isDragging = true"
                                    @dragleave.prevent="isDragging = false"
                                    @drop.prevent="handleDrop"
                                    @click="fileInputRef && fileInputRef.click()"
                                >
                                    <input
                                        ref="fileInputRef"
                                        id="file"
                                        type="file"
                                        multiple
                                        class="hidden"
                                        @change="handleFileInputChange"
                                        accept=".PDF,.PNG,.JPG,.JPEG,.WEBP,.XLSX,.DOCX,.PPTX,.ZIP,.RAR"
                                    />

                                    <div class="w-14 h-14 mb-3 rounded-2xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center shadow-inner">
                                        <i class="fa-solid fa-cloud-arrow-up text-2xl"></i>
                                    </div>

                                    <h4 class="text-sm md:text-base font-bold text-gray-700 dark:text-gray-200">
                                        Arrastra y suelta tus archivos aquí o <span class="text-blue-600 dark:text-blue-400 hover:underline">haz clic para examinar</span>
                                    </h4>
                                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Soporta múltiples archivos: PDF, Imágenes, Office y Comprimidos (máx. {{ limitSizeMB }}MB por archivo)
                                    </p>
                                </div>

                                <!-- Lista de Archivos Seleccionados -->
                                <div v-if="selectedFiles.length > 0" class="mt-4 space-y-2">
                                    <div class="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                        Archivos seleccionados ({{ selectedFiles.length }})
                                    </div>
                                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <div
                                            v-for="(f, idx) in selectedFiles"
                                            :key="idx"
                                            class="flex items-center justify-between p-2.5 rounded-xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/60 text-xs"
                                        >
                                            <div class="flex items-center gap-2 truncate pr-2">
                                                <i class="fa-solid fa-file text-blue-500 text-sm shrink-0"></i>
                                                <span class="font-medium text-gray-700 dark:text-gray-200 truncate">{{ f.name }}</span>
                                                <span class="text-gray-400 shrink-0">({{ formatFileSize(f.size) }})</span>
                                            </div>
                                            <button
                                                type="button"
                                                @click.stop="removeFile(idx)"
                                                class="text-gray-400 hover:text-red-500 p-1 rounded transition-colors shrink-0"
                                                title="Quitar archivo"
                                            >
                                                <i class="fa-solid fa-xmark"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Vista previa de imagen única -->
                                <div v-if="isImagen && imgMiniatura" class="mt-4 flex justify-center">
                                    <img :src="imgMiniatura" alt="Vista previa" class="max-h-48 rounded-xl object-contain shadow border border-gray-200" />
                                </div>
                            </div>

                            <!-- 2. NOMBRE Y DESCRIPCIÓN -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <BreezeLabel for="name" value="Nombre" :required="true" />
                                    <BreezeInput id="name" type="text" class="mt-1 block w-full" v-model="form.name" required autofocus autocomplete="Nombre" />
                                    <p v-if="selectedFiles.length > 1" class="text-[11px] text-gray-500 mt-1 italic">
                                        * En subida múltiple, cada archivo mantendrá su nombre individual.
                                    </p>
                                </div>

                                <div>
                                    <BreezeLabel for="position" value="Posición" :required="true" />
                                    <BreezeInput id="position" ref="position" type="number" class="mt-1 block w-full" v-model="form.position" required autocomplete="position" />
                                </div>
                            </div>

                            <div>
                                <BreezeLabel for="description" value="Descripción" />
                                <BreezeInput id="description" type="text" class="mt-1 block w-full" v-model="form.description" autocomplete="descripción" placeholder="Opcional" />
                            </div>

                            <!-- 3. DESTINO (Nivel o Curso con SelectSearch jerárquico) -->
                            <div>
                                <template v-if="origin_type == 'file_guideforcourse'">
                                    <BreezeLabel for="src_object_id" value="Curso relacionado a la guía de estudio" :required="true" class="mb-1" />
                                </template>
                                <template v-else-if="origin_type == 'file_forlevel'">
                                    <BreezeLabel for="src_object_id" value="Nivel escolar al que se mostrará este archivo" :required="true" class="mb-1" />
                                </template>
                                <template v-else>
                                    <BreezeLabel for="src_object_id" value="Destino del archivo" :required="true" class="mb-1" />
                                </template>

                                <SelectSearch
                                    v-model="form.src_object_id"
                                    :options="targetOptions"
                                    :getOptionValue="(opt) => opt.id"
                                    :getOptionLabel="(opt) => opt.label || opt.label_object"
                                    placeholder="Seleccione el nivel o curso..."
                                />
                            </div>

                            <!-- 4. FECHAS -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <BreezeLabel for="start_date" value="Fecha de Inicio" />
                                    <BreezeInput id="start_date" ref="start_date" type="date" class="mt-1 block w-full" v-model="form.start_date" autocomplete="start_date" />
                                </div>
                                <div>
                                    <BreezeLabel for="end_date" value="Fecha de Fin" />
                                    <BreezeInput id="end_date" ref="end_date" type="date" class="mt-1 block w-full" v-model="form.end_date" autocomplete="end_date" />
                                </div>
                            </div>

                            <!-- Botones -->
                            <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-800">
                                <Link :href="route('file-listado', origin_type)" class="btn btn-outline-gray">
                                    Volver al listado
                                </Link>

                                <BreezeButton class="ml-2" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                                    <i class="fa-solid fa-cloud-arrow-up mr-2"></i> Registrar
                                </BreezeButton>
                            </div>
                        </form>
                    </template>

                    <!-- Formulario de Edición -->
                    <template v-else>
                        <form @submit.prevent="submit" enctype="multipart/form-data" class="space-y-6">
                            
                            <!-- Archivo actual o Actualización -->
                            <div>
                                <BreezeLabel for="file_edit" value="Actualizar Archivo (Opcional)" class="mb-2 block font-semibold text-gray-700 dark:text-gray-200" />
                                <div
                                    class="border-2 border-dashed rounded-2xl p-4 text-center cursor-pointer bg-gray-50/60 dark:bg-slate-800/40 hover:bg-blue-50/50"
                                    @click="fileInputRef && fileInputRef.click()"
                                >
                                    <input
                                        ref="fileInputRef"
                                        id="file_edit"
                                        type="file"
                                        class="hidden"
                                        @change="handleFileInputChange"
                                        accept=".PDF,.PNG,.JPG,.JPEG,.WEBP,.XLSX,.DOCX,.PPTX,.ZIP,.RAR"
                                    />
                                    <div class="text-xs text-gray-600 dark:text-gray-300 flex items-center justify-center gap-2">
                                        <i class="fa-solid fa-arrow-up-from-bracket text-blue-500"></i>
                                        <span>Haz clic aquí si deseas reemplazar el archivo existente</span>
                                    </div>
                                    <div v-if="selectedFiles.length > 0" class="mt-2 text-xs font-semibold text-blue-600">
                                        Nuevo archivo seleccionado: {{ selectedFiles[0].name }}
                                    </div>
                                </div>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <BreezeLabel for="name" value="Nombre" :required="true" />
                                    <BreezeInput id="name" type="text" class="mt-1 block w-full" v-model="form.name" required autofocus autocomplete="Nombre" />
                                </div>
                                <div>
                                    <BreezeLabel for="position" value="Posición" :required="true" />
                                    <BreezeInput id="position" ref="position" type="number" class="mt-1 block w-full" v-model="form.position" required autocomplete="position" />
                                </div>
                            </div>

                            <div>
                                <BreezeLabel for="description" value="Descripción" />
                                <BreezeInput id="description" type="text" class="mt-1 block w-full" v-model="form.description" autocomplete="descripción" />
                            </div>

                            <div>
                                <template v-if="origin_type == 'file_guideforcourse'">
                                    <BreezeLabel for="src_object_id" value="Curso relacionado a la guía de estudio" :required="true" class="mb-1" />
                                </template>
                                <template v-else-if="origin_type == 'file_forlevel'">
                                    <BreezeLabel for="src_object_id" value="Nivel escolar al que se mostrará este archivo" :required="true" class="mb-1" />
                                </template>
                                <template v-else>
                                    <BreezeLabel for="src_object_id" value="Destino del archivo" :required="true" class="mb-1" />
                                </template>

                                <SelectSearch
                                    v-model="form.src_object_id"
                                    :options="targetOptions"
                                    :getOptionValue="(opt) => opt.id"
                                    :getOptionLabel="(opt) => opt.label || opt.label_object"
                                    placeholder="Seleccione el nivel o curso..."
                                />
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <BreezeLabel for="start_date" value="Fecha de Inicio" />
                                    <BreezeInput id="start_date" ref="start_date" type="date" class="mt-1 block w-full" v-model="form.start_date" autocomplete="start_date" />
                                </div>
                                <div>
                                    <BreezeLabel for="end_date" value="Fecha de Fin" />
                                    <BreezeInput id="end_date" ref="end_date" type="date" class="mt-1 block w-full" v-model="form.end_date" autocomplete="end_date" />
                                </div>
                            </div>

                            <!-- Vista previa del archivo actual -->
                            <div v-if="object && object.filepath && object.filename" class="p-4 rounded-xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/40">
                                <span class="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Archivo actual:</span>
                                <div class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {{ object.name || object.filename }}
                                </div>
                                <embed :src="'/documents/' + object.filepath + '/' + object.filename" class="w-full h-44 rounded-lg object-contain bg-white" />
                            </div>

                            <!-- Botones -->
                            <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-800">
                                <Link :href="route('file-listado', origin_type)" class="btn btn-outline-gray">
                                    Volver al listado
                                </Link>
                                <BaseButton v-if="deleteable" color="danger" label="Eliminar" small @click="initModal(object.id, 'eliminar')" class="ml-2" />
                                <BreezeButton class="ml-2" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                                    Actualizar
                                </BreezeButton>
                            </div>
                        </form>
                    </template>
                </div>
            </div>
        </div>

        <CardBoxModal
            v-model="isModalDangerActive"
            title="Confirmar eliminación"
            button="danger"
            hasCancel
            @confirm="isConfirm"
            @cancel="isCancel"
        >
            <p>¿Está seguro de que desea <b>eliminar</b> este documento de forma permanente?</p>
        </CardBoxModal>
    </BreezeAuthenticatedLayout>
</template>
